import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2, ScanSearch } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

// Lenders page toolbar: keyword search across all lender Notes/guidelines/programs + admin "scan all Notes" extraction.
export const GuidelineSearchBar = ({ isAdmin, onOpenLender, onScanned }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try { const r = await api.get('/lender-guidelines/search', { params: { q } }); setHits(r.data); } catch (err) { toast.error(err.response?.data?.detail || 'Search failed'); } finally { setBusy(false); }
  };
  const scanAll = async () => {
    setScanning(true);
    try {
      const r = await api.post('/lender-guidelines/extract-all', {});
      const ex = r.data.results.filter((x) => x.status === 'pending_review');
      toast.success(`Scanned ${r.data.scanned} lenders — ${ex.length} with new programs to review: ${ex.map((x) => x.lender_name).join(', ') || 'none'}`);
      onScanned?.(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Scan failed'); } finally { setScanning(false); }
  };

  return (
    <div className="space-y-2" data-testid="guideline-search">
      <form onSubmit={search} className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search lender guidelines (e.g. ground up, 90% LTC, first-time investor, Texas)…" value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) setHits(null); }} data-testid="guideline-search-input" />
        </div>
        <Button type="submit" variant="outline" disabled={busy || q.trim().length < 2} data-testid="guideline-search-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search guidelines'}</Button>
        {isAdmin && (
          <Button type="button" variant="secondary" onClick={scanAll} disabled={scanning} data-testid="guideline-scan-all-btn">
            {scanning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reading all Notes…</> : <><ScanSearch className="h-4 w-4 mr-1" /> Extract programs from all Notes</>}
          </Button>
        )}
      </form>
      {hits && (
        <div className="border rounded-lg p-3 bg-muted/40 text-sm space-y-2" data-testid="guideline-search-results">
          <p className="text-xs text-muted-foreground">{hits.hits.length} lender(s) mention “{hits.query}” (searched {hits.lenders_searched})</p>
          {hits.hits.map((h) => (
            <div key={h.lender_id} className="border-b last:border-0 pb-2">
              <button type="button" className="font-medium text-primary hover:underline" onClick={() => onOpenLender?.(h.lender_id)} data-testid={`guideline-hit-${h.lender_id}`}>{h.lender_name}</button>
              <ul className="text-xs text-muted-foreground mt-0.5 space-y-0.5">{h.snippets.map((s, i) => <li key={i}><span className="font-medium">{s.where}:</span> {s.text}</li>)}</ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuidelineSearchBar;
